/**
 * Source Tracker - Captures visitor source attribution for Rx.ChatWithDr.com
 * Detects: Paid ads, Organic search, AI search, Social, Email, Referral, Direct
 * Auto-detects ANY referrer domain, not just pre-mapped ones
 */

const SESSION_KEY = 'rxcwd_visitor_source';
const SESSION_ID_KEY = 'rxcwd_session_id';

// Generate unique session ID
const generateSessionId = () => {
  return 'vs_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
};

// ===== SOURCE CLASSIFICATION MAPS =====

// Search engines -> Organic Search
const SEARCH_ENGINES = {
  'google.com': 'Google', 'google.co': 'Google', 'www.google': 'Google',
  'bing.com': 'Bing', 'www.bing.com': 'Bing',
  'duckduckgo.com': 'DuckDuckGo',
  'yahoo.com': 'Yahoo', 'search.yahoo': 'Yahoo',
  'yandex.com': 'Yandex', 'yandex.ru': 'Yandex',
  'baidu.com': 'Baidu',
  'ecosia.org': 'Ecosia',
  'startpage.com': 'Startpage',
  'brave.com': 'Brave Search', 'search.brave.com': 'Brave Search',
  'qwant.com': 'Qwant',
  'naver.com': 'Naver',
  'sogou.com': 'Sogou',
  'aol.com': 'AOL',
  'ask.com': 'Ask',
};

// AI Platforms -> AI Search
const AI_PLATFORMS = {
  'chatgpt.com': 'ChatGPT', 'chat.openai.com': 'ChatGPT',
  'claude.ai': 'Claude',
  'gemini.google.com': 'Gemini',
  'perplexity.ai': 'Perplexity',
  'copilot.microsoft.com': 'Microsoft Copilot',
  'poe.com': 'Poe',
  'you.com': 'You.com',
  'phind.com': 'Phind',
  'meta.ai': 'Meta AI',
  'bard.google.com': 'Gemini',
  'aitastic.com': 'AI Search',
  'labs.google.com': 'Google AI',
  'grok.x.ai': 'Grok',
};

// Social platforms -> Social
const SOCIAL_PLATFORMS = {
  'facebook.com': 'Facebook', 'fb.com': 'Facebook', 'l.facebook.com': 'Facebook', 'lm.facebook.com': 'Facebook',
  'm.facebook.com': 'Facebook', 'web.facebook.com': 'Facebook',
  'instagram.com': 'Instagram', 'l.instagram.com': 'Instagram',
  'twitter.com': 'X (Twitter)', 'x.com': 'X (Twitter)', 't.co': 'X (Twitter)',
  'tiktok.com': 'TikTok', 'www.tiktok.com': 'TikTok',
  'reddit.com': 'Reddit', 'old.reddit.com': 'Reddit', 'www.reddit.com': 'Reddit',
  'linkedin.com': 'LinkedIn', 'www.linkedin.com': 'LinkedIn', 'lnkd.in': 'LinkedIn',
  'pinterest.com': 'Pinterest',
  'snapchat.com': 'Snapchat',
  'youtube.com': 'YouTube', 'youtu.be': 'YouTube', 'www.youtube.com': 'YouTube',
  'threads.net': 'Threads',
  'bsky.app': 'Bluesky',
  'mastodon.social': 'Mastodon',
  'discord.com': 'Discord', 'discord.gg': 'Discord',
  'telegram.org': 'Telegram', 't.me': 'Telegram',
  'whatsapp.com': 'WhatsApp',
  'nextdoor.com': 'Nextdoor',
  'quora.com': 'Quora',
};

// Paid ad indicators (UTM medium values)
const PAID_MEDIUMS = ['cpc', 'ppc', 'paid', 'paidsocial', 'paid_social', 'display', 'cpv', 'cpm', 'cpa', 'retargeting', 'remarketing', 'shopping'];

// Paid source mapping
const PAID_SOURCES = {
  'google': 'Google Ads', 'googleads': 'Google Ads', 'google_ads': 'Google Ads', 'adwords': 'Google Ads',
  'facebook': 'Meta (Facebook/Instagram)', 'fb': 'Meta (Facebook/Instagram)', 'meta': 'Meta (Facebook/Instagram)',
  'instagram': 'Meta (Facebook/Instagram)', 'ig': 'Meta (Facebook/Instagram)',
  'tiktok': 'TikTok Ads', 'tiktok_ads': 'TikTok Ads',
  'snapchat': 'Snapchat Ads', 'snap': 'Snapchat Ads',
  'reddit': 'Reddit Ads',
  'linkedin': 'LinkedIn Ads',
  'twitter': 'X (Twitter) Ads', 'x': 'X (Twitter) Ads',
  'bing': 'Microsoft Ads', 'microsoft': 'Microsoft Ads', 'msn': 'Microsoft Ads',
  'pinterest': 'Pinterest Ads',
  'youtube': 'YouTube Ads',
};

// ===== DETECTION LOGIC =====

const getUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_content: params.get('utm_content') || '',
    utm_term: params.get('utm_term') || '',
    gclid: params.get('gclid') || '',    // Google Ads click ID
    fbclid: params.get('fbclid') || '',   // Facebook click ID
    ttclid: params.get('ttclid') || '',   // TikTok click ID
    sclid: params.get('sclid') || '',     // Snapchat click ID
    msclkid: params.get('msclkid') || '', // Microsoft Ads click ID
  };
};

const extractDomain = (url) => {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return hostname.replace(/^www\./, '');
  } catch { return ''; }
};

const matchDomain = (domain, map) => {
  // Exact match first
  if (map[domain]) return map[domain];
  // Try with www
  if (map['www.' + domain]) return map['www.' + domain];
  // Try partial match (for subdomains like l.facebook.com)
  for (const [key, value] of Object.entries(map)) {
    if (domain.endsWith(key) || domain.includes(key.replace('www.', ''))) return value;
  }
  return null;
};

const detectDevice = () => {
  const ua = navigator.userAgent.toLowerCase();
  if (/tablet|ipad|playbook|silk/.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android(?!.*tablet)|blackberry|opera mini|iemobile/.test(ua)) return 'mobile';
  return 'desktop';
};

const detectBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox/')) return 'Firefox';
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('OPR/') || ua.includes('Opera')) return 'Opera';
  if (ua.includes('Chrome/') && !ua.includes('Edg/')) return 'Chrome';
  if (ua.includes('Safari/') && !ua.includes('Chrome/')) return 'Safari';
  if (ua.includes('MSIE') || ua.includes('Trident/')) return 'IE';
  return 'Other';
};

const detectOS = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac OS')) return 'macOS';
  if (ua.includes('Linux') && !ua.includes('Android')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  if (ua.includes('CrOS')) return 'ChromeOS';
  return 'Other';
};

/**
 * Classify the visitor source based on UTM params, referrer, and click IDs
 */
const classifySource = () => {
  const params = getUrlParams();
  const referrer = document.referrer || '';
  const referrerDomain = extractDomain(referrer);

  // 1. Check for ad platform click IDs first (most reliable)
  if (params.gclid) return { type: 'Paid', platform: 'Google Ads', referrer, referrerDomain };
  if (params.fbclid && !referrerDomain) return { type: 'Paid', platform: 'Meta (Facebook/Instagram)', referrer, referrerDomain };
  if (params.ttclid) return { type: 'Paid', platform: 'TikTok Ads', referrer, referrerDomain };
  if (params.sclid) return { type: 'Paid', platform: 'Snapchat Ads', referrer, referrerDomain };
  if (params.msclkid) return { type: 'Paid', platform: 'Microsoft Ads', referrer, referrerDomain };

  // 2. Check UTM parameters
  if (params.utm_source || params.utm_medium) {
    const utmSource = params.utm_source.toLowerCase();
    const utmMedium = params.utm_medium.toLowerCase();

    // Is it paid?
    if (PAID_MEDIUMS.includes(utmMedium)) {
      const platform = PAID_SOURCES[utmSource] || `${params.utm_source} Ads`;
      return { type: 'Paid', platform, referrer, referrerDomain };
    }

    // Is it email?
    if (utmMedium === 'email' || utmSource === 'email' || utmSource === 'newsletter') {
      return { type: 'Email', platform: params.utm_source || 'Email', referrer, referrerDomain };
    }

    // Is it social (organic)?
    if (utmMedium === 'social' || utmMedium === 'organic_social') {
      const platform = SOCIAL_PLATFORMS[utmSource] || PAID_SOURCES[utmSource]?.replace(' Ads', '') || params.utm_source;
      return { type: 'Social', platform, referrer, referrerDomain };
    }

    // Is it referral?
    if (utmMedium === 'referral') {
      return { type: 'Referral', platform: params.utm_source || referrerDomain || 'Unknown', referrer, referrerDomain };
    }
  }

  // 3. Classify by referrer domain
  if (referrerDomain) {
    // Skip own domain
    if (referrerDomain.includes('chatwithdr') || referrerDomain.includes('localhost')) {
      // Internal navigation - check if we have a stored source
      const stored = getStoredSource();
      if (stored) return stored;
      return { type: 'Direct', platform: 'Direct', referrer, referrerDomain };
    }

    // AI platform?
    const aiPlatform = matchDomain(referrerDomain, AI_PLATFORMS);
    if (aiPlatform) return { type: 'AI Search', platform: aiPlatform, referrer, referrerDomain };

    // Search engine?
    const searchEngine = matchDomain(referrerDomain, SEARCH_ENGINES);
    if (searchEngine) return { type: 'Organic Search', platform: searchEngine, referrer, referrerDomain };

    // Social platform?
    const socialPlatform = matchDomain(referrerDomain, SOCIAL_PLATFORMS);
    if (socialPlatform) {
      // If fbclid present, it's paid from facebook
      if (params.fbclid) return { type: 'Paid', platform: 'Meta (Facebook/Instagram)', referrer, referrerDomain };
      return { type: 'Social', platform: socialPlatform, referrer, referrerDomain };
    }

    // Any other external domain = Referral with the domain as platform name
    return { type: 'Referral', platform: referrerDomain, referrer, referrerDomain };
  }

  // 4. No referrer, no UTM = Direct
  return { type: 'Direct', platform: 'Direct', referrer: '', referrerDomain: '' };
};

// ===== SESSION MANAGEMENT =====

const getStoredSource = () => {
  try {
    const data = sessionStorage.getItem(SESSION_KEY);
    return data ? JSON.parse(data) : null;
  } catch { return null; }
};

const getSessionId = () => {
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = generateSessionId();
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
};

/**
 * Initialize source tracking - call once on app load
 * Returns the source data for the current session
 */
export const initSourceTracking = () => {
  const sessionId = getSessionId();

  // Only classify on first page view of session (or if UTM params present on this page)
  const params = getUrlParams();
  const hasNewUTM = params.utm_source || params.gclid || params.fbclid || params.ttclid;
  const existing = getStoredSource();

  let sourceData;
  if (!existing || hasNewUTM) {
    sourceData = classifySource();
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(sourceData));
  } else {
    sourceData = existing;
  }

  return {
    sessionId,
    source: sourceData,
    utm: {
      source: params.utm_source,
      medium: params.utm_medium,
      campaign: params.utm_campaign,
      content: params.utm_content,
      term: params.utm_term,
    },
    landingPage: window.location.pathname + window.location.search,
    device: detectDevice(),
    browser: detectBrowser(),
    os: detectOS(),
    userAgent: navigator.userAgent,
  };
};

/**
 * Send tracking data to server
 */
export const trackVisitor = async (sourceData) => {
  try {
    await fetch('/api/visitors/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sourceData),
    });
  } catch (err) {
    console.error('Visitor tracking failed:', err);
  }
};

/**
 * Get current source data for registration forms
 */
export const getSourceData = () => {
  const sessionId = getSessionId();
  const source = getStoredSource() || { type: 'Direct', platform: 'Direct' };
  const params = getUrlParams();
  return {
    sessionId,
    sourceType: source.type,
    sourcePlatform: source.platform,
    referrer: source.referrer || '',
    utmSource: params.utm_source,
    utmMedium: params.utm_medium,
    utmCampaign: params.utm_campaign,
    utmContent: params.utm_content,
    utmTerm: params.utm_term,
    landingPage: sessionStorage.getItem('rxcwd_landing_page') || window.location.pathname,
  };
};

/**
 * Store the initial landing page (call on first load)
 */
export const storeLandingPage = () => {
  if (!sessionStorage.getItem('rxcwd_landing_page')) {
    sessionStorage.setItem('rxcwd_landing_page', window.location.pathname + window.location.search);
  }
};
